import type { FC } from 'react'

import { Wizard, WizardStep, LoaderCircle } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useAdminAccess } from '@/hooks/access'
import { PolicyContextProvider, useNewPolicyContext } from '@/modules/ecom-policy/new/index.context'
import StepType from '@/modules/ecom-policy/new/steps/step-type/StepType'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'
import { APP_ROUTES } from '@/util/app-routes'

import StepCustomer from './steps/StepCustomer'
import StepName from './steps/StepName'
import StepVendor from './steps/StepVendor'
import type { Classification, CreatePolicyPayload } from './types'

const NewPolicy: FC = () => {
  useAdminAccess()

  const navigate = useNavigate()
  const { setIsWizardMode } = useAppContext()
  const newPolicy = useNewPolicyContext()
  const [selectedClassifications] = newPolicy.selectedClassifications
  const [customerHeadId] = newPolicy.selectedCustomerHeadId
  const [customerId] = newPolicy.selectedCustomerId
  const [name] = newPolicy.selectedName
  const [descriptionInfo] = newPolicy.descriptionInfo
  const [vendorInfo] = newPolicy.selectedVendorInfo

  const isCustomerStepValid = Boolean(customerId || customerHeadId)
  const isVendorStepValid = isCustomerStepValid && Boolean(vendorInfo.vendorId)
  const isNameStepValid = Boolean(name && descriptionInfo.description)

  const { isLoading } = useQuery(queries.customerData(), ecomPolicyResources.getCustomerData)
  const createPolicyMutation = useMutation(
    (policyPayload: CreatePolicyPayload & { classificationList: Classification[] | null }) =>
      ecomPolicyResources.createPolicy(policyPayload),
    {
      onSuccess({ policyId }: { policyId: number }) {
        navigate(APP_ROUTES.HARDWARE_ECOM_POLICY.replace(':ecomPolicyId', String(policyId)))
      },
    }
  )

  const postPolicy = async () => {
    if (!vendorInfo.ecomCustomerId || !vendorInfo.vendorId) return

    const payload: CreatePolicyPayload = {
      name,
      customerHeadId: customerHeadId || undefined,
      vendorId: vendorInfo.vendorId,
      customerId: customerId || undefined,
      ecomCustomerId: null,
      description: descriptionInfo.description,
    }

    if (!customerId) delete payload.customerId

    const classificationList = selectedClassifications?.includes('ALL')
      ? null
      : selectedClassifications

    createPolicyMutation.mutate({ ...payload, classificationList })
  }

  return (
    <Container className="ecom-policy-new">
      <Container.Header>
        <Translation id="Set up new policy" />
      </Container.Header>

      {isLoading ? (
        <LoaderCircle />
      ) : (
        <Wizard
          skipButtonTextDefault={<Translation id="Skip" />}
          nextButtonTextDefault={<Translation id="Next Step" />}
          previousButtonTextDefault={<Translation id="Previous Step" />}
          isLoading={createPolicyMutation.isLoading}
          onIsWizardModeSet={setIsWizardMode}
          onComplete={postPolicy}
        >
          <WizardStep name={<Translation id="Customer" />} isValid={isCustomerStepValid}>
            <StepCustomer />
          </WizardStep>

          <WizardStep
            name={<Translation id="Policy type" />}
            // Policy type is optional
            isValid={true}
          >
            <StepType />
          </WizardStep>

          <WizardStep name={<Translation id="Vendor" />} isValid={isVendorStepValid}>
            <StepVendor />
          </WizardStep>

          <WizardStep
            name={<Translation id="Policy Name" />}
            nextButtonText={<Translation id="Create Policy" />}
            isValid={isNameStepValid}
          >
            <StepName />
          </WizardStep>
        </Wizard>
      )}
    </Container>
  )
}

export default () => (
  <PolicyContextProvider>
    <NewPolicy />
  </PolicyContextProvider>
)
